import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PoweredByFooter from "../components/PoweredByFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Users, RefreshCw, ArrowLeft, Clock, Crown } from "lucide-react";
import { Calendar, ChevronDown, ChevronUp, User, Briefcase } from "lucide-react";
import { Printer, Filter, Phone, Mail, Edit2, X, Save } from "lucide-react";

export default function Vagtplaner({ user, onLogout }) {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState([]);
  const [mainRoles, setMainRoles] = useState([]);
  const [roles, setRoles] = useState([]); // Teams
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [expandedRoles, setExpandedRoles] = useState({});
  const [selectedDate, setSelectedDate] = useState('all'); // Dato-filter
  const [editingShiftTime, setEditingShiftTime] = useState(null); // {volunteerId, shiftId, startTime, endTime}
  const printRef = useRef(null);
  const eventId = localStorage.getItem("selectedEventId");
  const eventName = localStorage.getItem("selectedEventName");
  const token = localStorage.getItem("token");

  // Lookup funktion til at finde team-navn fra ID
  const getTeamName = (roleId) => {
    if (!roleId) return null;
    const team = roles.find(r => r.id === roleId);
    return team?.name || roleId;
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [eventId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [volunteersRes, mainRolesRes, shiftsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/volunteers/list`, { headers }),
        axios.get(`${API}/events/${eventId}/main-roles`, { headers }),
        axios.get(`${API}/events/${eventId}/shifts`, { headers }),
        axios.get(`${API}/events/${eventId}/roles`, { headers })
      ]);
      
      setVolunteers(volunteersRes.data);
      setMainRoles(mainRolesRes.data);
      setShifts(shiftsRes.data);
      setRoles(rolesRes.data);
      setLastUpdate(new Date());
      
      // Expand alle funktioner som standard
      const expanded = {};
      mainRolesRes.data.forEach(mr => {
        expanded[mr.id] = true;
      });
      setExpandedRoles(expanded);
    } catch (error) {
      console.error("Kunne ikke hente vagtplan data", error);
      toast.error("Fejl ved hentning af vagtplan");
    } finally {
      setLoading(false);
    }
  };

  // Gem ændret vagttid for en frivillig
  const saveShiftTimeOverride = async () => {
    if (!editingShiftTime) return;
    
    try {
      await axios.patch(
        `${API}/events/${eventId}/volunteers/${editingShiftTime.volunteerId}/shift-time`,
        {
          shift_id: editingShiftTime.shiftId,
          start_time: editingShiftTime.startTime,
          end_time: editingShiftTime.endTime
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success("Vagttid opdateret");
      setEditingShiftTime(null);
      loadData(); // Genindlæs data
    } catch (error) {
      console.error("Kunne ikke opdatere vagttid", error);
      toast.error("Fejl ved opdatering af vagttid");
    }
  };

  // Hent alle unikke datoer fra vagter
  const getUniqueDates = () => {
    const dates = new Set();
    shifts.forEach(shift => {
      if (shift.date) dates.add(shift.date);
    });
    return Array.from(dates).sort();
  };

  // Formatér dato til visning
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return 'Ingen dato';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('da-DK', { 
      weekday: 'long', 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Formatér kort dato
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('da-DK', { 
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  // Gruppér frivillige efter funktion
  const getGroupedData = () => {
    const grouped = {};
    
    // Initialiser med alle funktioner
    mainRoles.forEach(mr => {
      grouped[mr.id] = {
        mainRole: mr,
        leaders: [],
        volunteersByShift: {} // Grupperet efter vagt
      };
    });
    
    // Tilføj "Ikke tildelt" gruppe
    grouped['unassigned'] = {
      mainRole: { id: 'unassigned', name: 'Ikke tildelt funktion' },
      leaders: [],
      volunteersByShift: {}
    };
    
    // Sortér frivillige ind i grupper
    volunteers.forEach(v => {
      const mainRoleId = v.main_role_id || 'unassigned';
      
      if (!grouped[mainRoleId]) {
        grouped[mainRoleId] = {
          mainRole: { id: mainRoleId, name: 'Ukendt funktion' },
          leaders: [],
          volunteersByShift: {}
        };
      }
      
      // Tjek om personen er leder FOR denne funktion
      if (v.is_leader && v.leader_for_main_role_id === mainRoleId) {
        grouped[mainRoleId].leaders.push(v);
      } else if (v.shift_ids && v.shift_ids.length > 0) {
        // Tilføj til hver vagt de er på
        v.shift_ids.forEach(shiftId => {
          const shift = shifts.find(s => s.id === shiftId);
          if (shift) {
            // Filter på dato hvis valgt
            if (selectedDate !== 'all' && shift.date !== selectedDate) return;
            
            if (!grouped[mainRoleId].volunteersByShift[shiftId]) {
              grouped[mainRoleId].volunteersByShift[shiftId] = {
                shift,
                volunteers: []
              };
            }
            grouped[mainRoleId].volunteersByShift[shiftId].volunteers.push(v);
          }
        });
      }
    });
    
    // Sortér frivillige i hver vagt efter rolle, derefter navn
    Object.keys(grouped).forEach(roleId => {
      // Sortér ledere
      grouped[roleId].leaders.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '', 'da')
      );
      
      // Sortér frivillige i hver vagt
      Object.keys(grouped[roleId].volunteersByShift).forEach(shiftId => {
        grouped[roleId].volunteersByShift[shiftId].volunteers.sort((a, b) => {
          const roleA = a.role || '';
          const roleB = b.role || '';
          if (roleA !== roleB) return roleA.localeCompare(roleB, 'da');
          return (a.name || '').localeCompare(b.name || '', 'da');
        });
      });
    });
    
    return grouped;
  };

  const toggleRole = (roleId) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleId]: !prev[roleId]
    }));
  };

  // Print funktion
  const handlePrint = (mainRoleId, mainRoleName) => {
    const dateText = selectedDate === 'all' ? 'Alle dage' : formatDateForDisplay(selectedDate);
    const printWindow = window.open('', '_blank');
    
    const data = groupedData[mainRoleId];
    if (!data) return;
    
    // Generer HTML til print
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Vagtplan - ${mainRoleName} - ${dateText}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 24px; margin-bottom: 5px; }
          h2 { font-size: 14px; color: #666; margin-bottom: 20px; }
          .leaders { background: #fff8e1; padding: 10px; margin-bottom: 20px; border-radius: 5px; }
          .leaders h3 { margin: 0 0 5px 0; font-size: 12px; color: #f59e0b; }
          .date-section { margin-bottom: 30px; page-break-inside: avoid; }
          .date-header { background: #e8f5e9; padding: 8px 12px; font-weight: bold; margin-bottom: 10px; border-radius: 5px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th { background: #f5f5f5; padding: 8px; text-align: left; border-bottom: 2px solid #ddd; }
          td { padding: 8px; border-bottom: 1px solid #eee; }
          .role-badge { background: #e3f2fd; padding: 2px 8px; border-radius: 3px; font-size: 11px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${mainRoleName}</h1>
        <h2>${eventName} - ${dateText}</h2>
    `;
    
    // Ledere (kun navn - GDPR)
    if (data.leaders.length > 0) {
      html += `<div class="leaders"><h3>LEDERE</h3>`;
      html += data.leaders.map(l => l.name).join(', ');
      html += `</div>`;
    }
    
    // Gruppér vagter efter dato
    const shiftsByDate = {};
    Object.values(data.volunteersByShift).forEach(({ shift, volunteers }) => {
      const dateKey = shift.date || 'no-date';
      if (!shiftsByDate[dateKey]) {
        shiftsByDate[dateKey] = [];
      }
      shiftsByDate[dateKey].push({ shift, volunteers });
    });
    
    // Sortér datoer
    const sortedDates = Object.keys(shiftsByDate).sort();
    
    sortedDates.forEach(dateKey => {
      const shiftsOnDate = shiftsByDate[dateKey];
      shiftsOnDate.sort((a, b) => (a.shift.start_time || '').localeCompare(b.shift.start_time || ''));
      
      // Beregn statistik for denne dag
      const volunteersOnDay = new Set();
      const leadersOnDay = new Set();
      shiftsOnDate.forEach(({ volunteers }) => {
        volunteers.forEach(v => {
          volunteersOnDay.add(v.id);
          if (v.is_leader) leadersOnDay.add(v.id);
        });
      });
      const volunteerCount = volunteersOnDay.size;
      const leaderCount = leadersOnDay.size;
      
      html += `<div class="date-section">`;
      html += `<div class="date-header">${formatDateForDisplay(dateKey)} <span style="float: right; font-weight: normal; font-size: 12px;">${volunteerCount} frivillige${leaderCount > 0 ? `, ${leaderCount} ledere` : ''}</span></div>`;
      html += `<table>
        <thead>
          <tr>
            <th>Vagt/Tid</th>
            <th>Rolle</th>
            <th>Navn</th>
          </tr>
        </thead>
        <tbody>
      `;
      
      shiftsOnDate.forEach(({ shift, volunteers }) => {
        volunteers.forEach((v, idx) => {
          html += `
            <tr>
              ${idx === 0 ? `<td rowspan="${volunteers.length}">${shift.start_time}-${shift.end_time}</td>` : ''}
              <td><span class="role-badge">${v.role || '-'}</span></td>
              <td>${v.name}</td>
            </tr>
          `;
        });
      });
      
      html += `</tbody></table></div>`;
    });
    
    html += `</body></html>`;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const groupedData = getGroupedData();
  const uniqueDates = getUniqueDates();
  const totalVolunteers = volunteers.length;
  const totalLeaders = volunteers.filter(v => v.is_leader).length;

  // Tæl frivillige på valgt dato
  const getVolunteersOnDate = () => {
    if (selectedDate === 'all') return totalVolunteers;
    return volunteers.filter(v => {
      if (!v.shift_ids) return false;
      return v.shift_ids.some(shiftId => {
        const shift = shifts.find(s => s.id === shiftId);
        return shift && shift.date === selectedDate;
      });
    }).length;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="vagtplan-page">
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="mr-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">Vagtplan</h1>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                {eventName || "Alle events"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={loadData} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Opdater
            </Button>
            <div className="text-right">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground font-mono uppercase">{user?.role}</p>
            </div>
            <Button onClick={onLogout} variant="ghost" size="sm">Log ud</Button>
          </div>
        </div>
        
        {/* Dato-filter og statistik - sticky under header */}
        <div className="container mx-auto px-4 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-muted-foreground" />
                <select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-10 px-4 bg-card border border-border rounded-lg text-white font-medium"
                  data-testid="date-filter"
                >
                  <option value="all">Alle dage</option>
                  {uniqueDates.map(date => (
                    <option key={date} value={date}>{formatDateForDisplay(date)}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <span><strong>{getVolunteersOnDate()}</strong> frivillige</span>
              </div>
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                <span><strong>{totalLeaders}</strong> ledere</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {lastUpdate ? `Opdateret ${lastUpdate.toLocaleTimeString('da-DK')}` : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">

        {/* Vagtplan efter funktion */}
        {loading ? (
          <div className="text-center py-16">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Henter vagtplan...</p>
          </div>
        ) : Object.keys(groupedData).length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-lg">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Ingen frivillige endnu</h3>
            <p className="text-muted-foreground">
              Tilføj frivillige og tildel dem vagter for at se vagtplanen
            </p>
          </div>
        ) : (
          <div className="space-y-6" ref={printRef}>
            {Object.entries(groupedData)
              .filter(([_, data]) => {
                // Vis kun hvis der er ledere eller vagter med frivillige
                return data.leaders.length > 0 || Object.keys(data.volunteersByShift).length > 0;
              })
              .sort((a, b) => {
                if (a[0] === 'unassigned') return 1;
                if (b[0] === 'unassigned') return -1;
                return (a[1].mainRole.name || '').localeCompare(b[1].mainRole.name || '', 'da');
              })
              .map(([roleId, data]) => {
                // Tæl antal frivillige
                const volunteerCount = Object.values(data.volunteersByShift)
                  .reduce((acc, { volunteers }) => acc + volunteers.length, 0);
                
                return (
                  <div
                    key={roleId}
                    className="bg-card border border-border rounded-lg overflow-hidden"
                    data-testid={`mainrole-section-${roleId}`}
                  >
                    {/* Funktion header */}
                    <div className="flex items-center justify-between bg-indigo-500/10 border-b border-indigo-500/20">
                      <button
                        onClick={() => toggleRole(roleId)}
                        className="flex-1 p-4 flex items-center gap-3 hover:bg-indigo-500/20 transition-colors text-left"
                      >
                        <Briefcase className="h-6 w-6 text-indigo-500" />
                        <div>
                          <h3 className="font-bold text-lg">{data.mainRole.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {data.leaders.length} {data.leaders.length === 1 ? 'leder' : 'ledere'} • {volunteerCount} på vagt
                          </p>
                        </div>
                        {expandedRoles[roleId] ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground ml-auto" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground ml-auto" />
                        )}
                      </button>
                      
                      {/* Print knap */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePrint(roleId, data.mainRole.name)}
                        className="mr-4"
                        title="Udskriv denne funktion"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Indhold */}
                    {expandedRoles[roleId] && (
                      <div className="p-4">
                        {/* Ledere */}
                        {data.leaders.length > 0 && (
                          <div className="mb-6 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                            <h4 className="text-xs uppercase tracking-wider font-mono text-yellow-500 flex items-center gap-2 mb-3">
                              <Crown className="h-4 w-4" />
                              Ledere
                            </h4>
                            <div className="flex flex-wrap gap-3">
                              {data.leaders.map(leader => (
                                <div key={leader.id} className="flex items-center gap-2 bg-yellow-500/20 px-3 py-2 rounded-lg">
                                  <Crown className="h-4 w-4 text-yellow-500" />
                                  <span className="font-medium">{leader.name}</span>
                                  {leader.phone && (
                                    <a href={`tel:${leader.phone}`} className="text-xs text-muted-foreground hover:text-primary">
                                      <Phone className="h-3 w-3 inline mr-1" />{leader.phone}
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Vagter grupperet efter dato */}
                        {Object.keys(data.volunteersByShift).length === 0 ? (
                          <p className="text-sm text-muted-foreground italic text-center py-4">
                            Ingen frivillige på vagt {selectedDate !== 'all' ? 'denne dag' : ''}
                          </p>
                        ) : (
                          (() => {
                            // Gruppér vagter efter dato
                            const shiftsByDate = {};
                            Object.values(data.volunteersByShift).forEach(({ shift, volunteers }) => {
                              const dateKey = shift.date || 'no-date';
                              if (!shiftsByDate[dateKey]) {
                                shiftsByDate[dateKey] = [];
                              }
                              shiftsByDate[dateKey].push({ shift, volunteers });
                            });
                            
                            return Object.entries(shiftsByDate)
                              .sort(([a], [b]) => a.localeCompare(b))
                              .map(([dateKey, shiftsOnDate]) => {
                                // Beregn statistik for denne dag
                                const volunteersOnDay = new Set();
                                const leadersOnDay = new Set();
                                shiftsOnDate.forEach(({ volunteers }) => {
                                  volunteers.forEach(v => {
                                    volunteersOnDay.add(v.id);
                                    if (v.is_leader) leadersOnDay.add(v.id);
                                  });
                                });
                                const dayVolunteerCount = volunteersOnDay.size;
                                const dayLeaderCount = leadersOnDay.size;
                                
                                return (
                                <div key={dateKey} className="mb-6 last:mb-0">
                                  {/* Dato header med statistik */}
                                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3 mb-3">
                                    <h5 className="font-bold text-green-400 flex items-center gap-2">
                                      <Calendar className="h-5 w-5" />
                                      {formatDateForDisplay(dateKey)}
                                      <span className="text-sm font-normal text-green-400/80 ml-2">
                                        — {dayLeaderCount} {dayLeaderCount === 1 ? 'leder' : 'ledere'}, {dayVolunteerCount} på vagt
                                      </span>
                                    </h5>
                                  </div>
                                  
                                  {/* Tabel for denne dato */}
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-muted/30">
                                        <tr>
                                          <th className="text-left p-3 font-medium w-36">Tid</th>
                                          <th className="text-left p-3 font-medium">Team</th>
                                          <th className="text-left p-3 font-medium">Navn</th>
                                          <th className="text-left p-3 font-medium">Telefon</th>
                                          <th className="text-left p-3 font-medium">Email</th>
                                          <th className="text-center p-3 font-medium w-16">Rediger</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {shiftsOnDate
                                          .sort((a, b) => (a.shift.start_time || '').localeCompare(b.shift.start_time || ''))
                                          .map(({ shift, volunteers }) => (
                                            volunteers.map((v, idx) => {
                                              // Tjek om frivillig har individuel vagttid
                                              const override = v.shift_time_overrides?.[shift.id];
                                              const displayStart = override?.start_time || shift.start_time;
                                              const displayEnd = override?.end_time || shift.end_time;
                                              const hasOverride = override?.start_time || override?.end_time;
                                              
                                              return (
                                              <tr 
                                                key={`${shift.id}-${v.id}`}
                                                className={idx % 2 === 0 ? 'bg-background/50' : ''}
                                              >
                                                <td className="p-3 font-medium">
                                                  <span className={hasOverride ? 'text-yellow-400' : 'text-green-400'}>
                                                    {displayStart}-{displayEnd}
                                                  </span>
                                                  {hasOverride && (
                                                    <span className="text-xs text-muted-foreground ml-1">(tilpasset)</span>
                                                  )}
                                                </td>
                                                <td className="p-3">
                                                  {v.role ? (
                                                    <span className="text-xs px-2 py-1 rounded bg-primary/20 text-primary">
                                                      {getTeamName(v.role)}
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                  )}
                                                </td>
                                                <td className="p-3 font-medium">{v.name}</td>
                                                <td className="p-3">
                                                  {v.phone ? (
                                                    <a href={`tel:${v.phone}`} className="text-muted-foreground hover:text-primary flex items-center gap-1">
                                                      <Phone className="h-3 w-3" />
                                                      {v.phone}
                                                    </a>
                                                  ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                  )}
                                                </td>
                                                <td className="p-3">
                                                  {v.email ? (
                                                    <a href={`mailto:${v.email}`} className="text-muted-foreground hover:text-primary flex items-center gap-1">
                                                      <Mail className="h-3 w-3" />
                                                      {v.email}
                                                    </a>
                                                  ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                  )}
                                                </td>
                                                <td className="p-3 text-center">
                                                  <button
                                                    onClick={() => setEditingShiftTime({
                                                      volunteerId: v.id,
                                                      volunteerName: v.name,
                                                      shiftId: shift.id,
                                                      shiftName: shift.name,
                                                      startTime: displayStart,
                                                      endTime: displayEnd,
                                                      originalStart: shift.start_time,
                                                      originalEnd: shift.end_time
                                                    })}
                                                    className="p-1.5 hover:bg-muted rounded transition-colors"
                                                    title="Rediger vagttid"
                                                  >
                                                    <Edit2 className="h-4 w-4 text-muted-foreground hover:text-primary" />
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                            })
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                              })
                          })()
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
        <PoweredByFooter />
      </main>
      
      {/* Rediger vagttid dialog */}
      {editingShiftTime && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Rediger vagttid</h3>
              <button 
                onClick={() => setEditingShiftTime(null)}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-muted/30 p-3 rounded-lg">
                <p className="font-medium">{editingShiftTime.volunteerName}</p>
                <p className="text-sm text-muted-foreground">
                  Vagt: {editingShiftTime.shiftName}
                </p>
                <p className="text-xs text-muted-foreground">
                  Standard tid: {editingShiftTime.originalStart}-{editingShiftTime.originalEnd}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Start tid</label>
                  <Input
                    type="time"
                    value={editingShiftTime.startTime || ''}
                    onChange={(e) => setEditingShiftTime({
                      ...editingShiftTime,
                      startTime: e.target.value
                    })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Slut tid</label>
                  <Input
                    type="time"
                    value={editingShiftTime.endTime || ''}
                    onChange={(e) => setEditingShiftTime({
                      ...editingShiftTime,
                      endTime: e.target.value
                    })}
                  />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Denne tid overskriver standardtiden for vagten kun for denne frivillig.
              </p>
              
              <div className="flex justify-between pt-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditingShiftTime({
                    ...editingShiftTime,
                    startTime: editingShiftTime.originalStart,
                    endTime: editingShiftTime.originalEnd
                  })}
                >
                  Nulstil til standard
                </Button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditingShiftTime(null)}>
                    Annuller
                  </Button>
                  <Button onClick={saveShiftTimeOverride}>
                    <Save className="h-4 w-4 mr-2" />
                    Gem
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
